import { Box, Typography, IconButton, Collapse, Divider, Link, CircularProgress, Fab } from '@mui/material';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { PureLightThemeCaaS } from 'src/theme/schemes/PureLightTheme';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { glossaryData, flattenGlossaryForSeo } from 'src/utils/helpMenuContent';
import { Browser } from '@capacitor/browser';
import { isPlatform } from '@ionic/react';
import TextFieldHelp from 'src/components/TextField/TextFieldHelp';
import { PROD_FRONTEND_URL, WEB_FRONTEND_LINK_PATTERNS } from 'src/config/frontendUrls';
import { Helmet } from 'react-helmet-async';
import {
  SEO_GLOSSARY_DESCRIPTION,
  SEO_GLOSSARY_TITLE,
  SEO_OG_IMAGE,
  SEO_SITE_NAME,
  SEO_TWITTER_HANDLE,
  buildGlossaryCanonicalUrl,
} from 'src/config/seo.config';


interface GlossaryProps {
    handleBack: () => void;
    handleClose: () => void;
    standalone?: boolean;
}

// Helper function to handle store redirects with explicit store type
const handleStoreClick = async (e: React.MouseEvent, storeType: 'appstore' | 'playstore') => {
    e.preventDefault();
    e.stopPropagation();


    // URLs for each store
    // const appStoreUrl = "https://apps.apple.com/search?q=RotoGPT"; 
    // const playStoreUrl = "https://play.google.com/store/search?q=RotoGPT&c=apps";

    // REAL ONES:
    const appStoreUrl = "https://apps.apple.com/us/app/rotogpt/id6736617092";
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.rotogpt.chat";

    // Use the explicitly specified store type
    const url = storeType === 'appstore' ? appStoreUrl : playStoreUrl;

    try {
        if (isPlatform("hybrid")) {
            // Use Capacitor Browser for native platforms
            await Browser.open({ url });
        } else {
            // Fallback for web platform
            window.open(url, "_blank");
        }
    } catch (error) {
        console.error("Error opening store:", error);
        // Additional fallback: try to open in new tab
        try {
            window.open(url, "_blank");
        } catch (fallbackError) {
            console.error("Fallback also failed:", fallbackError);
        }
    }
};

// Helper function to handle web link
const handleWebClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(PROD_FRONTEND_URL, '_blank');
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text: string) => {
    // Patterns to match: App Store, Google Play, iOS, Android, RotoGPT.com, chat.rotogpt.com (legacy)
    const patterns = [
        { pattern: /App Store/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'appstore') },
        { pattern: /Google Play/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'playstore') },
        // { pattern: /iOS/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'appstore') },
        // { pattern: /Android/g, handler: (e: React.MouseEvent) => handleStoreClick(e, 'playstore') },
        // { pattern: /RotoGPT\.com/g, handler: handleWebClick },
        ...WEB_FRONTEND_LINK_PATTERNS.map(({ pattern }) => ({ pattern, handler: handleWebClick })),
    ];

    let result: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyCounter = 0;

    // Find all matches
    const matches: Array<{ start: number; end: number; pattern: typeof patterns[0] }> = [];
    patterns.forEach(({ pattern, handler }) => {
        let match;
        while ((match = pattern.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                pattern: { pattern, handler },
            });
        }
    });

    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);

    // Build result with links
    matches.forEach((match) => {
        // Add text before match
        if (match.start > lastIndex) {
            result.push(text.substring(lastIndex, match.start));
        }

        // Add link
        const linkText = text.substring(match.start, match.end);
        result.push(
            <Link
                key={keyCounter++}
                component="span"
                onClick={match.pattern.handler}
                sx={{
                    color: 'blue',
                    cursor: 'pointer',
                    display: 'inline',
                    textDecoration: 'none',
                    verticalAlign: 'baseline',
                    lineHeight: 'inherit',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                }}
            >
                {linkText}
            </Link>
        );

        lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
        result.push(text.substring(lastIndex));
    }

    return result.length > 0 ? result : text;
};

// Helper function to render answer text with bullet points and links
const renderAnswer = (answer: string) => {
    const lines = answer.split('\n');
    return lines.map((line, index) => {
        // Check if line starts with bullet point
        if (line.trim().startsWith('•')) {
            return (
                <Box
                    key={index}
                    component="span"
                    sx={{
                        display: 'block',
                        // pl: 2,
                        p: 0.2,
                    }}
                >
                    {renderTextWithLinks(line)}
                </Box>
            );
        }
        // Regular line with links
        return (
            <React.Fragment key={index}>
                {renderTextWithLinks(line)}
                {index < lines.length - 1}
            </React.Fragment>
        );
    });
};

// Escape special regex chars for use in RegExp (same as FAQ)
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Highlight search term in plain text (same as FAQ)
const highlightSearch = (text: string, search: string): React.ReactNode => {
    if (!search.trim()) return text;
    const lower = search.toLowerCase().trim();
    const parts = text.split(new RegExp(`(${escapeRegex(search)})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === lower ? (
            <Box key={i} component="span" sx={{ bgcolor: "#f99067", borderRadius: 0 }}>{part}</Box>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        )
    );
};

// Answer with search term highlighted, keeps links and bullet structure (same as FAQ)
const renderAnswerWithHighlight = (answer: string, search: string): React.ReactNode => {
    if (!search.trim()) return renderAnswer(answer);
    const lower = search.toLowerCase().trim();
    const lines = answer.split('\n');
    return lines.map((line, lineIdx) => {
        const parts = line.split(new RegExp(`(${escapeRegex(search)})`, 'gi'));
        const content = parts.map((part, i) =>
            part.toLowerCase() === lower ? (
                <Box key={i} component="span" sx={{ bgcolor: "#f99067", borderRadius: 0 }}>{part}</Box>
            ) : (
                <React.Fragment key={i}>{renderTextWithLinks(part)}</React.Fragment>
            )
        );
        if (line.trim().startsWith('•')) {
            return <Box key={lineIdx} component="span" sx={{ display: 'block', p: 0.2 }}>{content}</Box>;
        }
        return <React.Fragment key={lineIdx}>{content}{lineIdx < lines.length - 1 ? <br /> : null}</React.Fragment>;
    });
};

const ITEMS_PER_PAGE = 100;

const Glossary = ({ handleBack, handleClose, standalone = false }: GlossaryProps) => {
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    const [searchText, setSearchText] = useState("");
    const [numItemsToShow, setNumItemsToShow] = useState(ITEMS_PER_PAGE);
    const [showLoader, setShowLoader] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const isLoadingMoreRef = useRef(false);

    const scrollableRef = useRef<HTMLDivElement>(null);
    // Same as RankingsResponse: scroll the Box with ref (the scroll container)
    const scrollToTop = () => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    };


    // Flatten all glossary questions into one array with stable id (same logic as Rankings data flow)
    const allQuestions = useMemo(() => {
        const list: { id: number; q: string; a: string }[] = [];
        glossaryData.forEach((category) => {
            category.subCategories?.forEach((sub) => {
                sub.questions.forEach((question) => {
                    const answerText = typeof question.a === "function" ? question.a() : question.a;
                    list.push({ id: list.length, q: question.q, a: answerText });
                });
            });
        });
        return list;
    }, []);

    const filteredQuestions = useMemo(() => {
        if (!searchText.trim()) return allQuestions;
        const lower = searchText.toLowerCase().trim();
        return allQuestions.filter(
            (item) => item.q.toLowerCase().includes(lower) || item.a.toLowerCase().includes(lower)
        );
    }, [allQuestions, searchText]);

    const loadedQuestions = useMemo(
        () => filteredQuestions.slice(0, numItemsToShow),
        [filteredQuestions, numItemsToShow]
    );

    // Reset load more when search changes (same as Rankings reset on filter change)
    useEffect(() => {
        setNumItemsToShow(ITEMS_PER_PAGE);
    }, [searchText]);

    // When search is cleared: collapse all. When search has value: expand all result items (same as FAQ)
    useEffect(() => {
        if (!searchText.trim()) {
            setExpandedQuestions(new Set());
        } else {
            const ids = new Set(filteredQuestions.map((item) => item.id));
            setExpandedQuestions(ids);
        }
    }, [searchText, filteredQuestions]);

    // In this useEffect an observer is used to detect when load more (same as RankingsResponse)
    useEffect(() => {
        if (!filteredQuestions.length) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (
                    !entry.isIntersecting ||
                    isLoadingMoreRef.current ||
                    showLoader ||
                    loadedQuestions.length >= filteredQuestions.length
                )
                    return;
                isLoadingMoreRef.current = true;
                setShowLoader(true);
                setTimeout(() => {
                    const newCount = loadedQuestions.length + ITEMS_PER_PAGE;
                    setNumItemsToShow(
                        newCount > filteredQuestions.length ? filteredQuestions.length : newCount
                    );
                    setShowLoader(false);
                    isLoadingMoreRef.current = false;
                }, 1000);
            },
            { root: null, threshold: 1.0 }
        );
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [loadedQuestions, filteredQuestions, showLoader]);

    // Toggle question: when searching allow multiple open; when not searching only one open (same as FAQ)
    const handleToggleQuestion = (id: number) => {
        if (searchText.trim()) {
            setExpandedQuestions((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        } else {
            setExpandedQuestions((prev) => (prev.has(id) ? new Set() : new Set([id])));
        }
    };

    // Same structure as RankingsResponse: wrapper with position absolute so scroll happens inside our Box (not parent)
    const canonicalUrl = buildGlossaryCanonicalUrl(process.env.REACT_APP_FRONTEND_URI_WEB || PROD_FRONTEND_URL);
    const glossaryJsonLd = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: SEO_GLOSSARY_TITLE,
        description: SEO_GLOSSARY_DESCRIPTION,
        hasDefinedTerm: flattenGlossaryForSeo().slice(0, 100).map(({ q, a }) => ({
            "@type": "DefinedTerm",
            name: q,
            description: a,
        })),
    });

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                bgcolor: "#EDEDED",
            }}
        >
            {standalone ? (
                <Helmet>
                    <title>{SEO_GLOSSARY_TITLE}</title>
                    <meta name="description" content={SEO_GLOSSARY_DESCRIPTION} />
                    <link rel="canonical" href={canonicalUrl} />
                    <meta property="og:type" content="website" />
                    <meta property="og:site_name" content={SEO_SITE_NAME} />
                    <meta property="og:title" content={SEO_GLOSSARY_TITLE} />
                    <meta property="og:description" content={SEO_GLOSSARY_DESCRIPTION} />
                    <meta property="og:url" content={canonicalUrl} />
                    <meta property="og:image" content={SEO_OG_IMAGE} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" content={SEO_TWITTER_HANDLE} />
                    <meta name="twitter:title" content={SEO_GLOSSARY_TITLE} />
                    <meta name="twitter:description" content={SEO_GLOSSARY_DESCRIPTION} />
                    <meta name="twitter:image" content={SEO_OG_IMAGE} />
                    <script type="application/ld+json">{glossaryJsonLd}</script>
                </Helmet>
            ) : null}
            {/* Scrollable content - ref for scrollToTop (same as RankingsResponse scrollableRef) */}
            <Box
                ref={scrollableRef}
                sx={{
                    flex: 1,
                    width: "100%",
                    minWidth: 0,
                    overflowY: "auto",
                    minHeight: 0,
                    p: 3,
                    pt: 1,
                    pb: 4,
                    WebkitOverflowScrolling: "touch",
                    bgcolor: "#EDEDED",
                    scrollbarWidth: "none",
                }}
            >
                <Typography
                    variant="h5"
                    color={PureLightThemeCaaS.colors.alpha.black[100]}
                    sx={{ fontSize: "20px", fontWeight: "bold", mt: standalone ? 0 : 8, mb: 1, textAlign: "center" }}
                >
                    {glossaryData[0]?.category ?? "Fantasy Football Glossary"}
                </Typography>

                {/* Search - same styles as Help via TextFieldHelp */}
                <Box sx={{ bgcolor: "#EDEDED", pb: 0 }}>
                    <TextFieldHelp
                        searchText={searchText}
                        setSearchText={setSearchText}
                        label="Search terms"
                    />
                </Box>

                {filteredQuestions.length === 0 ? (
                    <Typography
                        variant="body2"
                        color={PureLightThemeCaaS.colors.alpha.black[100]}
                        sx={{ py: 2 }}
                    >
                        No terms match your search.
                    </Typography>
                ) : (
                    <>
                        {loadedQuestions.map((item, index) => {
                            const isExpanded = expandedQuestions.has(item.id);
                            const shouldShowDivider = index < loadedQuestions.length - 1;

                            return (
                                <Box key={item.id} sx={{ width: "100%" }}>
                                    <Box
                                        onClick={() => handleToggleQuestion(item.id)}
                                        sx={{
                                            cursor: "pointer",
                                            py: 0.5,
                                            width: "100%",
                                            bgcolor: "#EDEDED",
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color={PureLightThemeCaaS.colors.alpha.black[100]}
                                            sx={{
                                                fontSize: "15px",
                                                fontWeight: "medium",
                                                flex: 1,
                                                textAlign: "left",
                                                pr: 2,
                                            }}
                                        >
                                            {searchText.trim()
                                                ? highlightSearch(item.q, searchText.trim())
                                                : item.q}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: PureLightThemeCaaS.colors.alpha.black[100],
                                                flexShrink: 0,
                                                position: "relative",
                                                width: 40,
                                                height: 40,
                                                "&:hover": { bgcolor: "transparent" },
                                                "&:active": { bgcolor: "transparent" },
                                                "&.Mui-focusVisible": { bgcolor: "transparent" },
                                            }}
                                        >
                                            <AddIcon
                                                sx={{
                                                    fontSize: "20px",
                                                    position: "absolute",
                                                    opacity: isExpanded ? 0 : 1,
                                                    transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                                    transition:
                                                        "opacity 0.25s ease-in-out, transform 0.3s ease-in-out",
                                                }}
                                            />
                                            <RemoveIcon
                                                sx={{
                                                    fontSize: "20px",
                                                    position: "absolute",
                                                    opacity: isExpanded ? 1 : 0,
                                                    transition: "opacity 0.25s ease-in-out",
                                                }}
                                            />
                                        </IconButton>
                                    </Box>

                                    <Collapse in={isExpanded} timeout={300}>
                                        <Box sx={{ px: 1, pb: 1, pt: 0, bgcolor: "#EDEDED" }}>
                                            <Typography
                                                variant="body2"
                                                color={PureLightThemeCaaS.colors.alpha.black[100]}
                                                sx={{ fontSize: "14px", lineHeight: 1.3, whiteSpace: "pre-line" }}
                                            >
                                                {searchText.trim()
                                                    ? renderAnswerWithHighlight(item.a, searchText.trim())
                                                    : renderAnswer(item.a)}
                                            </Typography>
                                        </Box>
                                    </Collapse>

                                    {shouldShowDivider && (
                                        <Divider
                                            sx={{
                                                width: "100%",
                                                mx: "auto",
                                                height: "1px",
                                                bgcolor: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                                                my: 0,
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                        {showLoader && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={25} sx={{ color: "#FF672D" }} />
                            </Box>
                        )}
                        {loadedQuestions.length < filteredQuestions.length && (
                            <Box ref={loadMoreRef} sx={{ width: "100%", height: "1px", minHeight: "1px" }} />
                        )}
                    </>
                )}
            </Box>

            {/* Same as ButtonScrollUp (Rankings): fixed within 720px width, aligned to content area */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 30,
                    left: "50%",
                    transform: "translateX(-50%)",
                    maxWidth: "720px",
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "28px",
                    zIndex: 9999,
                    pointerEvents: "none",
                }}
            >
                <Fab
                    color="info"
                    aria-label="scroll to top"
                    onClick={scrollToTop}
                    size="small"
                    sx={{ pointerEvents: "auto" }}
                >
                    <ArrowUpwardIcon />
                </Fab>
            </Box>
        </Box>
    );
};

export default Glossary;
